// Pure helpers for the Email marketing sub-view: providers and credential
// fields straight from the plugin manifests, trigger labels, sync log
// statuses and form validation. No React/store imports.
import { BREVO_PLUGIN, KLAVIYO_PLUGIN, MAILCHIMP_PLUGIN, type PluginManifest } from '@dunea/shared';
import type { EmailProvider, EmailSyncStatus, EmailTrigger, EmailTriggers } from '~/lib/api/email-marketing';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export interface ProviderField {
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;
  required: boolean;
}

export interface EmailProviderSpec {
  id: EmailProvider;
  title: string;
  description: string;
  fields: ProviderField[];
}

function spec(manifest: PluginManifest): EmailProviderSpec {
  return {
    id: manifest.id as EmailProvider,
    title: manifest.title,
    description: manifest.description,
    fields: (manifest.settings ?? []).flatMap((group) =>
      group.fields.map((f) => ({
        key: f.key,
        label: f.label,
        placeholder: f.placeholder ?? '',
        secret: f.type === 'secret',
        required: f.required === true,
      })),
    ),
  };
}

export const EMAIL_PROVIDER_SPECS: readonly EmailProviderSpec[] = [KLAVIYO_PLUGIN, MAILCHIMP_PLUGIN, BREVO_PLUGIN].map(
  spec,
);

export function emailProviderSpec(id: string | null | undefined): EmailProviderSpec | undefined {
  return EMAIL_PROVIDER_SPECS.find((p) => p.id === id);
}

export const EMAIL_TRIGGERS: readonly { key: EmailTrigger; label: string; hint: string }[] = [
  { key: 'forms', label: 'Form leads', hint: 'People who submit a form on your site.' },
  { key: 'funnels', label: 'Funnel opt-ins', hint: 'People who opt in on a funnel step.' },
  { key: 'customers', label: 'Shop customers', hint: 'Buyers, after a paid order.' },
  { key: 'accounts', label: 'New accounts', hint: 'People who sign up for an account on your site.' },
];

export function emailTriggerLabel(key: string): string {
  return EMAIL_TRIGGERS.find((t) => t.key === key)?.label ?? key;
}

const REF_TRIGGERS: Record<string, EmailTrigger> = {
  forms: 'forms',
  funnels: 'funnels',
  'shop.order': 'customers',
  'auth.user': 'accounts',
};

/** Which trigger a sync log row came from, by its sourceRef prefix ("shop.order:…" → Shop customers). */
export function syncSourceLabel(sourceRef: string): string {
  const prefix = sourceRef.split(':')[0] ?? '';
  const trigger = REF_TRIGGERS[prefix];

  return trigger ? emailTriggerLabel(trigger) : prefix;
}

export function enabledTriggerCount(triggers: EmailTriggers): number {
  return EMAIL_TRIGGERS.filter((t) => triggers[t.key]).length;
}

const SYNC_META: Record<EmailSyncStatus, { label: string; tone: Tone }> = {
  syncing: { label: 'Syncing', tone: 'info' },
  synced: { label: 'Synced', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  skipped_no_consent: { label: 'No opt-in', tone: 'muted' },
  skipped_test_mode: { label: 'Test mode', tone: 'muted' },
};

export function syncStatusMeta(status: string): { label: string; tone: Tone } {
  return SYNC_META[status as EmailSyncStatus] ?? { label: status, tone: 'muted' };
}

/** One line on why a row was skipped, or null. */
export function syncStatusHint(status: string): string | null {
  switch (status) {
    case 'skipped_no_consent':
      return 'Not synced: the form had no marketing opt-in ticked.';
    case 'skipped_test_mode':
      return 'From the preview or a shop that is not live yet, so not sent.';
    default:
      return null;
  }
}

/**
 * Labels of required fields still missing. A secret already stored for the
 * same provider counts as present (it is write-only and never prefilled).
 */
export function missingEmailFields(
  provider: EmailProviderSpec,
  values: Record<string, string>,
  storedSecrets: readonly string[],
): string[] {
  return provider.fields
    .filter((f) => f.required && !values[f.key]?.trim() && !(f.secret && storedSecrets.includes(f.key)))
    .map((f) => f.label);
}

/** The credentials to send: every non-secret field, and secrets only when typed (empty keeps the stored one). */
export function emailCredentials(provider: EmailProviderSpec, values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};

  for (const f of provider.fields) {
    const v = values[f.key]?.trim() ?? '';

    if (!f.secret || v) {
      out[f.key] = v;
    }
  }

  return out;
}
